import {
  createRouter,
  createWebHistory,
  NavigationGuard,
  NavigationGuardReturn,
  RouteRecordRaw,
  RouteLocationNormalizedGeneric
} from 'vue-router'

import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'

import AuthLayout from '@/pages/Auth/AuthPage.vue'
import AuthOverview from '@/pages/Auth/Overview.vue'
import SignUpPage from '@/pages/Auth/SignUpPage.vue'
import Logout from '@/pages/Logout/index.vue'

import Invite from '@/pages/Invite/index.vue'

import Profile from '@/pages/User/index.vue'
import UserBridge from '@/pages/User/Bridge/index.vue'
import Dashboard from '@/pages/Dashboard/index.vue'

import FeatureLayout from '@/pages/Feature/layout.vue'
import FeatureIndex from '@/pages/Feature/index.vue'
import FeatureEdit from '@/pages/Feature/Edit/index.vue'

import OrgLayout from '@/pages/Organization/layout.vue'
import OrgMembers from '@/pages/Organization/Members/index.vue'
import OrgSettings from '@/pages/Organization/Settings/index.vue'
import OrgBilling from '@/pages/Organization/Billing/index.vue'
import ProjectLayout from '@/pages/Project/layout.vue'
import ProjectHome from '@/pages/Project/index.vue'
import ProjectSettings from '@/pages/Project/Settings/index.vue'
import ProjectTemplates from '@/pages/Project/Templates/index.vue'
import ProjectMembers from '@/pages/Project/Members/index.vue'
import ProjectEdit from '@/pages/Project/Edit/index.vue'
import ProjectCreate from '@/pages/Project/Create/index.vue'

import LayersLayout from '@/pages/Layers/layout.vue'
import EmptyLayout from '@/pages/Empty/layout.vue'

import Admin from '@/pages/Admin/index.vue'
import AdminUsers from '@/pages/Admin/Users/index.vue'
import AdminOrganizations from '@/pages/Admin/Organizations/index.vue'
import AdminTemplates from '@/pages/Admin/Templates/index.vue'
import AdminPayments from '@/pages/Admin/Payments/index.vue'
import AdminPlanet from '@/pages/Admin/Planet/index.vue'

import FileViewer from '@/pages/FileViewer/index.vue'

import LayerCreate from '@/pages/Layers/Create/index.vue'
import LayerDetail from '@/pages/Layers/Detail/index.vue'

import ReportDetail from '@/pages/Report/index.vue'
import NoService from '@/pages/Error/NoService.vue'

import { features } from '@/config/features'

// Wait for Firebase to restore auth state (resolves once on first call, instant after)
let authReady: Promise<void> | null = null
function waitForAuth(): Promise<void> {
  if (!authReady) {
    authReady = new Promise(resolve => {
      const unsubscribe = onAuthStateChanged(getAuth(), () => {
        unsubscribe()
        resolve()
      })
    })
  }
  return authReady
}

type RouteGuard = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
  ...args: any[]
) => NavigationGuardReturn | Promise<NavigationGuardReturn>

const nextPreserveQueryParams: RouteGuard = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
) => {
  // Exclude 'redirect' from preserved params to prevent stacking
  const { redirect, ...fromQuery } = from.query
  const isRedirectionNeeded = Object.keys(fromQuery).length > 0 && JSON.stringify(to.query) !== JSON.stringify(fromQuery)
  if (isRedirectionNeeded) {
    return { path: to.path, query: fromQuery }
  }
}

const requireAuth: RouteGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
) => {
  await waitForAuth()
  const auth = getAuth()
  const user = auth.currentUser

  if (!user) {
    return {
      path: '/auth',
      query: { redirect: to.fullPath }
    }
  } else {
    return nextPreserveQueryParams(to, from)
  }
}

const logout: NavigationGuard = async () => {
  const auth = getAuth()
  await signOut(auth)
}

const requireBillingEnabled: RouteGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
) => {
  if (!features.stripe.showBillingPage) {
    return { name: 'OrgMembers', params: to.params }
  } else {
    return requireAuth(to, from)
  }
}

const rts: RouteRecordRaw[] = [
  {
    path: '/auth',
    name: 'Auth',
    component: AuthLayout,
    meta: {
      layout: 'fullscreen',
      showBackgroundUI: false,
      title: 'Sign In'
    },
    children: [
      {
        path: 'overview',
        name: 'AuthOverview',
        component: AuthOverview,
        meta: {
          title: 'Select Organization',
          layout: 'fullscreen',
          showBackgroundUI: false
        }
      }
    ]
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    component: SignUpPage,
    meta: {
      layout: 'modal',
      showBackgroundUI: false,
      title: 'Sign Up'
    }
  },
  {
    path: '/invite/:inviteCode',
    name: 'Invite',
    component: Invite,
    meta: {
      title: 'Invite',
      layout: 'modal',
      showBackgroundUI: false
    },
  },
  {
    path: '/user',
    name: 'User',
    component: Profile,
    beforeEnter: requireAuth,
    meta: {
      title: 'Profile Settings',
    },
  },
  {
    path: '/user/bridge',
    name: 'UserBridge',
    component: UserBridge,
    beforeEnter: requireAuth,
    meta: {
      title: 'Payment Verification',
    },
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    beforeEnter: requireAuth,
    meta: {
      title: 'Dashboard',
      layout: 'hide'
    }
  },
  {
    path: '/organizations/:org_id/projects/create',
    name: 'CreateProject',
    component: ProjectCreate,
    beforeEnter: requireAuth,
    meta: {
      title: 'Create New Project',
    }
  },
  {
    path: '/organizations/:org_id/projects/:project_id/features/:featureCode',
    name: 'Feature',
    component: FeatureLayout,
    redirect: {name: 'FeatureIndex'},
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'FeatureIndex',
        component: FeatureIndex,
        meta: {
          title: 'Feature',
        }
      },
      {
        path: 'edit',
        name: 'FeatureEdit',
        component: FeatureEdit,
        meta: {
          title: 'Edit Feature',
        }
      }
    ]
  },
  {
    path: '/organizations/:org_id/file-viewer',
    name: 'FileViewer',
    component: FileViewer,
    beforeEnter: requireAuth,
    meta: {
      title: 'Import Data',
      layout: 'float-big'
    }
  },
  {
    path: '/organizations/:org_id',
    name: 'Organization',
    component: OrgLayout,
    redirect: {name: 'OrgProjects'},
    beforeEnter: requireAuth,
    meta: {
      layout: 'float-big'
    },
    children: [
      {
        path: 'members',
        name: 'OrgMembers',
        component: OrgMembers,
        meta: {
          title: 'Organization Members',
          layout: 'float-big'
        }
      },
      {
        path: 'settings',
        name: 'OrgSettings',
        component: OrgSettings,
        meta: {
          title: 'Organization Settings',
          layout: 'float-big'
        }
      },
      {
        path: 'billing',
        name: 'OrgBilling',
        component: OrgBilling,
        beforeEnter: requireBillingEnabled,
        meta: {
          title: 'Organization Billing',
          layout: 'float-big'
        }
      },
    ]
  },
  {
    path: '/organizations/:org_id/projects',
    name: 'OrgProjects',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Projects',
    }
  },
  {
    path: '/organizations/:org_id/layers',
    name: 'OrgLayers',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Layers',
    }
  },
  {
    path: '/organizations/:org_id/chats',
    name: 'OrgReports',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Chats',
    }
  },
  {
    path: '/organizations/:org_id/chats/:session_code',
    name: 'ChatSession',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Chat',
    }
  },
  {
    path: '/organizations/:org_id/reports',
    name: 'OrgReportsPage',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Reports',
    }
  },
  {
    path: '/organizations/:org_id/payments',
    name: 'OrgPayments',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Payments',
    }
  },
  {
    path: '/organizations/:org_id/reports/:report_uuid',
    name: 'ReportDetail',
    component: ReportDetail,
    beforeEnter: requireAuth,
    meta: {
      title: 'Report',
      layout: 'float-big'
    }
  },
  {
    path: '/organizations/:org_id/layers/create',
    name: 'LayerCreate',
    component: LayerCreate,
    beforeEnter: requireAuth,
    meta: {
      title: 'New Layer',
    }
  },
  {
    path: '/organizations/:org_id/layers/:layer_id',
    name: 'LayerDetail',
    component: LayerDetail,
    beforeEnter: requireAuth,
    meta: {
      title: 'Layer Detail',
    }
  },
  {
    path: '/organizations/:org_id/projects/:project_id',
    name: 'Project',
    component: ProjectLayout,
    redirect: {name: 'ProjectHome'},
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'ProjectHome',
        component: ProjectHome,
        meta: {
          title: 'Feed',
        }
      },
      {
        path: 'edit',
        name: 'ProjectEdit',
        component: ProjectEdit,
        meta: {
          title: 'Edit Project',
        }
      },
      {
        path: 'create',
        name: 'ProjectCreate',
        component: ProjectCreate,
        meta: {
          title: 'New Project',
        }
      },
      {
        path: 'settings',
        name: 'ProjectSettings',
        component: ProjectSettings,
        meta: {
          layout: 'float-big',
          title: 'Project Settings',
        }
      },
      {
        path: 'templates',
        name: 'ProjectTemplates',
        component: ProjectTemplates,
        meta: {
          layout: 'float-big',
          title: 'Project Templates',
        }
      },
      {
        path: 'members',
        name: 'ProjectMembers',
        component: ProjectMembers,
        meta: {
          layout: 'float-big',
          title: 'Project Members',
        }
      }
    ]
  },
  {
    path: '/admin/',
    name: 'Admin',
    component: Admin,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin Tools',
      layout: 'float-big'
    }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsers,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin - Members',
      layout: 'full'
    }
  },
  {
    path: '/admin/organizations',
    name: 'AdminOrganizations',
    component: AdminOrganizations,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin - Organizations',
      layout: 'full'
    }
  },
  {
    path: '/admin/templates',
    name: 'AdminTemplates',
    component: AdminTemplates,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin - Templates',
      layout: 'full'
    }
  },
  {
    path: '/admin/payments',
    name: 'AdminPayments',
    component: AdminPayments,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin - Payments',
      layout: 'fullscreen'
    }
  },
  {
    path: '/admin/bridge',
    redirect: '/admin/payments'
  },
  {
    path: '/admin/planet',
    name: 'AdminPlanet',
    component: AdminPlanet,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin - Planet Products',
      layout: 'full'
    }
  },
  {
    path: '/payments/me',
    redirect: '/admin/payments'
  },
  {
    path: '/logout',
    name: 'Logout',
    component: Logout,
    meta: {
      title: 'Logout'
    }
  },
  {
    path: '/no-service',
    name: 'NoService',
    component: NoService,
    meta: {
      title: 'Connection Error',
      layout: 'modal',
      showBackgroundUI: false
    }
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/',
    beforeEnter: requireAuth
  }
]

const baseUrl = window.location.pathname + window.location.search
let history = null
if (window.location.search.includes('oobCode')) {
  history = createWebHistory(baseUrl)
} else {
  history = createWebHistory()
}

export const router = createRouter({
  history: history,
  routes: rts
})

export default router
